import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import en from './locales/en';
import ru from './locales/ru';
import es from './locales/es';
import fr from './locales/fr';
import ko from './locales/ko';
import ar from './locales/ar';
import translationSources from './translationRegistry';

export const LOCALES = [
  { code: 'en', label: 'English', shortLabel: 'EN', dir: 'ltr' },
  { code: 'ru', label: 'Русский', shortLabel: 'RU', dir: 'ltr' },
  { code: 'es', label: 'Español', shortLabel: 'ES', dir: 'ltr' },
  { code: 'fr', label: 'Français', shortLabel: 'FR', dir: 'ltr' },
  { code: 'ko', label: '한국어', shortLabel: 'KO', dir: 'ltr' },
  { code: 'ar', label: 'العربية', shortLabel: 'AR', dir: 'rtl' },
];

const bundles = { en, ru, es, fr, ko, ar };
const STORAGE_KEY = 'romindkids.locale';
const DEFAULT_LOCALE = 'en';
const TRANSLATABLE_ATTRIBUTES = ['alt', 'title', 'placeholder', 'aria-label'];
const BLOCKED_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'CODE', 'PRE', 'TEXTAREA']);
const sourceTextByNode = new WeakMap();
const sourceAttributesByElement = new WeakMap();
const sourceKeys = new Set(translationSources.flatMap((source) => Object.values(source).flatMap((dictionary) => Object.keys(dictionary))));
const LocaleContext = createContext(null);
const getByPath = (object, path) => path.split('.').reduce((value, segment) => value?.[segment], object);
const interpolate = (value, vars = {}) => typeof value === 'string' ? value.replace(/\{(\w+)\}/g, (match, key) => (vars[key] ?? match)) : value;
const normalizeSource = (value) => String(value || '').replace(/\s+/g, ' ').trim();
const splitWhitespace = (raw) => ({ leading: raw.match(/^\s*/)?.[0] || '', trailing: raw.match(/\s*$/)?.[0] || '' });
const localeCodes = new Set(LOCALES.map((item) => item.code));

const writeStoredLocale = (locale) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // URL remains the source of truth when storage is unavailable.
  }
};

export const localeFromPath = (pathname = '/') => {
  const firstSegment = pathname.split('/').filter(Boolean)[0]?.toLowerCase();
  return localeCodes.has(firstSegment) ? firstSegment : DEFAULT_LOCALE;
};

export const stripLocalePrefix = (pathname = '/') => {
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length && localeCodes.has(parts[0].toLowerCase())) parts.shift();
  return parts.length ? `/${parts.join('/')}` : '/';
};

export const buildLocalizedPath = (locale, pathname = '/') => {
  const safeLocale = localeCodes.has(locale) ? locale : DEFAULT_LOCALE;
  const canonicalPath = stripLocalePrefix(pathname);
  if (safeLocale === DEFAULT_LOCALE) return canonicalPath;
  return canonicalPath === '/' ? `/${safeLocale}` : `/${safeLocale}${canonicalPath}`;
};

export const getLocaleBasename = (pathname = typeof window !== 'undefined' ? window.location.pathname : '/') => {
  const locale = localeFromPath(pathname);
  return locale === DEFAULT_LOCALE ? '/' : `/${locale}`;
};

const getContentTranslation = (locale, key) => {
  for (let index = translationSources.length - 1; index >= 0; index -= 1) {
    const translated = translationSources[index][locale]?.[key];
    if (translated != null) return translated;
  }
  return null;
};

const detectLocale = () => {
  if (typeof window === 'undefined') return DEFAULT_LOCALE;
  return localeFromPath(window.location.pathname);
};

const translateTextNode = (node, locale) => {
  const parent = node.parentElement;
  if (!parent || BLOCKED_TAGS.has(parent.tagName) || parent.closest('[data-i18n-skip="true"]')) return;
  let source = sourceTextByNode.get(node);
  if (!source) {
    const key = normalizeSource(node.nodeValue);
    if (!key || !sourceKeys.has(key)) return;
    source = { raw: node.nodeValue, key };
    sourceTextByNode.set(node, source);
  }
  const { leading, trailing } = splitWhitespace(source.raw);
  const translated = locale === DEFAULT_LOCALE ? normalizeSource(source.raw) : getContentTranslation(locale, source.key) ?? source.key;
  const nextValue = `${leading}${translated}${trailing}`;
  if (node.nodeValue !== nextValue) node.nodeValue = nextValue;
};

const translateElementAttributes = (element, locale) => {
  if (!(element instanceof Element) || element.closest('[data-i18n-skip="true"]')) return;
  let stored = sourceAttributesByElement.get(element);
  if (!stored) {
    stored = new Map();
    sourceAttributesByElement.set(element, stored);
  }
  TRANSLATABLE_ATTRIBUTES.forEach((attribute) => {
    if (!element.hasAttribute(attribute)) return;
    let source = stored.get(attribute);
    if (!source) {
      const raw = element.getAttribute(attribute) || '';
      const key = normalizeSource(raw);
      if (!key || !sourceKeys.has(key)) return;
      source = { raw, key };
      stored.set(attribute, source);
    }
    const translated = locale === DEFAULT_LOCALE ? source.raw : getContentTranslation(locale, source.key) ?? source.raw;
    if (element.getAttribute(attribute) !== translated) element.setAttribute(attribute, translated);
  });
};

const translatePageContent = (locale) => {
  document.querySelectorAll('main').forEach((root) => {
    translateElementAttributes(root, locale);
    root.querySelectorAll('*').forEach((element) => translateElementAttributes(element, locale));
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let node = walker.nextNode();
    while (node) {
      translateTextNode(node, locale);
      node = walker.nextNode();
    }
  });
};

const syncGiftLanguageControl = (locale) => {
  const select = document.getElementById('gift-language');
  if (!select) return;

  // Gift historically shipped with its own locale picker. The site-wide URL locale
  // is now authoritative, so keep that internal state in sync and hide the duplicate UI.
  const wrapper = select.parentElement;
  if (wrapper) wrapper.style.display = 'none';
  if (select.value === locale) return;

  const nativeSetter = Object.getOwnPropertyDescriptor(window.HTMLSelectElement.prototype, 'value')?.set;
  if (nativeSetter) nativeSetter.call(select, locale);
  else select.value = locale;
  select.dispatchEvent(new Event('change', { bubbles: true }));
};

export const LocaleProvider = ({ children }) => {
  const [locale, setLocaleState] = useState(detectLocale);

  const setLocale = useCallback((nextLocale) => {
    const safeLocale = bundles[nextLocale] ? nextLocale : DEFAULT_LOCALE;
    setLocaleState(safeLocale);
    if (typeof window === 'undefined') return;

    writeStoredLocale(safeLocale);
    const nextPath = buildLocalizedPath(safeLocale, window.location.pathname);
    if (nextPath !== window.location.pathname) {
      window.location.assign(`${nextPath}${window.location.search}${window.location.hash}`);
    }
  }, []);

  useEffect(() => {
    const meta = LOCALES.find((item) => item.code === locale) || LOCALES[0];
    document.documentElement.lang = locale;
    document.documentElement.dir = meta.dir;
    document.body.dataset.locale = locale;
    writeStoredLocale(locale);
  }, [locale]);

  useEffect(() => {
    syncGiftLanguageControl(locale);
    translatePageContent(locale);
    let scheduled = false;
    const observer = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(() => {
        scheduled = false;
        syncGiftLanguageControl(locale);
        translatePageContent(locale);
      });
    });
    observer.observe(document.body, { subtree: true, childList: true, characterData: true });
    return () => observer.disconnect();
  }, [locale]);

  const t = useCallback((key, vars) => {
    const localized = getByPath(bundles[locale], key);
    const fallback = getByPath(bundles[DEFAULT_LOCALE], key);
    return interpolate(localized ?? fallback ?? key, vars);
  }, [locale]);

  const value = useMemo(() => ({ locale, setLocale, t, locales: LOCALES, direction: LOCALES.find((item) => item.code === locale)?.dir || 'ltr' }), [locale, setLocale, t]);
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
};

export const useLocale = () => {
  const context = useContext(LocaleContext);
  if (!context) throw new Error('useLocale must be used inside LocaleProvider');
  return context;
};
